import React, { useEffect } from "react";
import { SiteLayout } from "@/components/layout/SiteLayout";
import { PageHero } from "@/components/layout/PageHero";
import { CtaBand } from "@/components/layout/CtaBand";
import { Section } from "@/components/layout/Section";
import { Reveal } from "@/components/common/Reveal";
import { LEGAL_CONTENT, LEGAL_ALIASES, LEGAL_EMAIL, LEGAL_UPDATED } from "@/content/pagesContent";

/**
 * LegalPage — renders official Strato Lab 3D legal / informative content.
 * Content source: STRATO_LEGAL_EMERGENT_PACKAGE.md (verbatim, no invented data).
 * Design is intentionally consistent with the existing site (no redesign).
 */

/* Linkify the official email inside a paragraph while keeping text verbatim. */
const renderParagraph = (text) => {
  if (!text.includes(LEGAL_EMAIL)) return text;
  const parts = text.split(LEGAL_EMAIL);
  return parts.map((part, i) => (
    <React.Fragment key={i}>
      {part}
      {i < parts.length - 1 ? (
        <a
          href={`mailto:${LEGAL_EMAIL}`}
          data-testid="legal-email-link"
          className="font-medium text-strato-blue underline decoration-strato-blue/30 underline-offset-2 transition-colors hover:decoration-strato-blue"
        >
          {LEGAL_EMAIL}
        </a>
      ) : null}
    </React.Fragment>
  ));
};

export default function LegalPage({ slug }) {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const key = LEGAL_ALIASES[slug] || slug;
  const page = LEGAL_CONTENT[key];

  if (!page) {
    return (
      <SiteLayout>
        <PageHero
          testId={`legal-${slug}-page`}
          micro="DOCUMENTO"
          title="Documento non trovato"
          description="Il documento richiesto non è disponibile."
        />
        <CtaBand secondaryTo="/contatti" secondaryLabel="Contattaci" />
      </SiteLayout>
    );
  }

  return (
    <SiteLayout>
      <PageHero
        testId={`legal-${key}-page`}
        micro={page.micro}
        title={page.title}
        description={page.intro}
      />

      <Section id="legal-body-section" tone="light">
        <Reveal>
          <article
            data-testid={`legal-body-${key}`}
            className="mx-auto max-w-2xl"
          >
            <div className="rounded-3xl border border-strato-gray-100 bg-strato-white p-7 shadow-soft sm:p-10">
              {page.sections.map((section, i) => (
                <section
                  key={i}
                  className={i > 0 ? "mt-9 border-t border-strato-gray-100 pt-9" : ""}
                >
                  {section.heading ? (
                    <h2 className="font-display text-xl font-medium tracking-[-0.01em] text-strato-graphite">
                      {section.heading}
                    </h2>
                  ) : null}
                  {section.paragraphs?.map((p, pi) => (
                    <p
                      key={pi}
                      className={
                        "text-[15px] leading-[1.7] text-muted-foreground " +
                        (section.heading || pi > 0 ? "mt-3" : "")
                      }
                    >
                      {renderParagraph(p)}
                    </p>
                  ))}
                  {section.list ? (
                    <ul className="mt-4 space-y-2">
                      {section.list.map((item, li) => (
                        <li
                          key={li}
                          className="flex gap-3 text-[15px] leading-[1.6] text-muted-foreground"
                        >
                          <span
                            aria-hidden
                            className="mt-2.5 h-1 w-1 flex-shrink-0 rounded-full bg-strato-blue"
                          />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                  {section.paragraphsAfter?.map((p, pi) => (
                    <p
                      key={`after-${pi}`}
                      className="mt-3 text-[15px] leading-[1.7] text-muted-foreground"
                    >
                      {renderParagraph(p)}
                    </p>
                  ))}
                </section>
              ))}

              <p
                data-testid="legal-updated"
                className="mt-9 border-t border-strato-gray-100 pt-6 text-xs text-muted-foreground"
              >
                {LEGAL_UPDATED}
              </p>
            </div>

            <p className="mt-6 text-center text-sm text-muted-foreground">
              Per domande scrivi a{" "}
              <a
                href={`mailto:${LEGAL_EMAIL}`}
                data-testid="legal-contact-link"
                className="font-medium text-strato-blue underline decoration-strato-blue/30 underline-offset-2 hover:decoration-strato-blue"
              >
                {LEGAL_EMAIL}
              </a>
              .
            </p>
          </article>
        </Reveal>
      </Section>
    </SiteLayout>
  );
}
